import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  ExternalLink,
  MessageSquare,
  Send,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import {
  useAddBatchComment,
  useBulkToggleBatchItems,
  useInviterCreatorRequestBatch,
  useToggleBatchItemCheck,
} from '@/hooks/useInviterCreatorRequestBatches';
import type { CreatorRequestStatus } from '@/api/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Pill } from '@/components/ui/pill';
import { Tag, channelToneFor, channelLabel } from '@/components/ui/tag';
import { PlatformIcon } from '@/components/PlatformIcon';
import { firstNameOf } from '@/lib/displayName';
import { cn } from '@/lib/utils';
import { requestStatusLabel } from '@/lib/labels';
import {
  batchStatusLabel,
  batchStatusTone,
  commentAuthorLabel,
  useTeamName,
} from '@/pages/CreatorRequestBatches';

const ITEM_DONE_STATUSES: ReadonlySet<CreatorRequestStatus> = new Set([
  'ROSTERED',
  'CANCELLED',
  'COMPLETED',
]);

/** Terminal statuses that shouldn't be toggled from the checkbox. They need
 *  a manual state change on the underlying request first (open it and act). */
const LOCKED_STATUSES: ReadonlySet<CreatorRequestStatus> = new Set([
  'ROSTERED',
  'CANCELLED',
]);

export function InviterCreatorRequestBatchDetailPage(): JSX.Element {
  const { publicId } = useParams<{ publicId: string }>();
  const query = useInviterCreatorRequestBatch(publicId ?? null);
  const toggleItem = useToggleBatchItemCheck();
  const bulkToggle = useBulkToggleBatchItems();
  const addComment = useAddBatchComment();
  const [draft, setDraft] = useState('');
  const teamName = useTeamName();

  const batch = query.data;

  if (query.isLoading) {
    return (
      <div className="space-y-4 p-6">
        <div className="h-9 w-72 rounded bg-muted animate-pulse" />
        <div className="h-48 w-full rounded bg-muted animate-pulse" />
      </div>
    );
  }
  if (!batch) {
    return <p className="p-6 text-sm text-destructive">Batch not found.</p>;
  }

  const pct = batch.itemCount === 0
    ? 0
    : Math.round((batch.checkedCount / batch.itemCount) * 100);

  async function handleToggle(itemPublicId: string, checked: boolean) {
    if (!batch) return;
    try {
      await toggleItem.mutateAsync({ publicId: batch.publicId, itemPublicId, checked });
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function handleMarkAllChecked() {
    if (!batch) return;
    const toggleable = batch.items.filter(
      (it) => !ITEM_DONE_STATUSES.has(it.status) && !LOCKED_STATUSES.has(it.status),
    );
    if (toggleable.length === 0) {
      toast.info('Nothing left to check off. Every creator is already done or locked.');
      return;
    }
    if (!confirm(`Mark all ${toggleable.length} remaining creators as checked?`)) return;
    try {
      await bulkToggle.mutateAsync({
        publicId: batch.publicId,
        itemPublicIds: toggleable.map((it) => it.publicId),
        checked: true,
      });
      toast.success(`Checked ${toggleable.length}`);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function postComment() {
    if (!batch) return;
    const body = draft.trim();
    if (!body) return;
    try {
      await addComment.mutateAsync({ publicId: batch.publicId, body });
      setDraft('');
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/portal/creator-requests">
            <ArrowLeft className="mr-1 h-3 w-3" /> Back to requests
          </Link>
        </Button>
      </div>

      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">{batch.name}</h1>
        <p className="text-sm text-muted-foreground">
          Submitted {formatDistanceToNow(new Date(batch.createdAt), { addSuffix: true })}{' '}
          by {firstNameOf(batch.requesterName) || batch.requesterName} · {batch.publicId}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Tag>{teamName(batch.targetTeam)}</Tag>
        {batch.channel && (
          <Tag tone={channelToneFor(batch.channel)}>{channelLabel(batch.channel)}</Tag>
        )}
        <Pill tone={batchStatusTone(batch.status)}>{batchStatusLabel(batch.status)}</Pill>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base">
              Progress · {batch.checkedCount}/{batch.itemCount}
            </CardTitle>
            {batch.status === 'OPEN' && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleMarkAllChecked}
                disabled={bulkToggle.isPending}
              >
                <CheckCircle2 className="mr-1 h-4 w-4" /> Mark all as checked
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                'h-full transition-all',
                batch.status === 'CANCELLED'
                  ? 'bg-red-500'
                  : pct === 100
                    ? 'bg-emerald-500'
                    : 'bg-blue-500',
              )}
              style={{ width: `${pct}%` }}
            />
          </div>
          {batch.brief && (
            <div className="rounded-md border bg-muted/30 p-3 text-sm">
              <p className="mb-1 text-xs font-medium text-muted-foreground">Shared brief</p>
              <p className="whitespace-pre-wrap">{batch.brief}</p>
            </div>
          )}
          {batch.status === 'CANCELLED' && batch.cancellationNote && (
            <div className="rounded-md border border-red-500/20 bg-red-500/5 p-3 text-sm">
              <p className="mb-1 text-xs font-medium text-red-400">Cancellation note</p>
              <p className="whitespace-pre-wrap">{batch.cancellationNote}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Creators</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ul className="divide-y">
            {batch.items.map((item) => {
              const done = ITEM_DONE_STATUSES.has(item.status);
              const locked = LOCKED_STATUSES.has(item.status);
              return (
                <li
                  key={item.id}
                  className="flex items-center justify-between gap-3 px-4 py-2.5"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <button
                      type="button"
                      onClick={() => handleToggle(item.publicId, !done)}
                      disabled={locked || batch.status !== 'OPEN'}
                      className={cn(
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded transition',
                        locked || batch.status !== 'OPEN'
                          ? 'cursor-not-allowed opacity-60'
                          : 'hover:opacity-80',
                      )}
                      aria-pressed={done}
                      aria-label={done ? 'Uncheck' : 'Check off'}
                    >
                      {done ? (
                        <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                      ) : (
                        <Circle className="h-5 w-5 text-muted-foreground" />
                      )}
                    </button>
                    {item.channel && (
                      <PlatformIcon
                        platform={item.channel}
                        size={14}
                        className="text-muted-foreground"
                      />
                    )}
                    <span
                      className={cn(
                        'truncate text-sm',
                        done && 'text-muted-foreground line-through',
                      )}
                    >
                      {item.targetIdentifier}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {requestStatusLabel(item.status)}
                    </span>
                    <Link
                      to={`/portal/creator-requests/${item.publicId}`}
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground underline-offset-4 hover:underline"
                    >
                      Open <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="h-4 w-4" /> Comments
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-h-[28rem] space-y-3 overflow-y-auto">
            {batch.comments.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No comments yet. Leave a note for the campaign manager who sent this.
              </p>
            ) : (
              batch.comments.map((c) => (
                <div key={c.id} className="rounded-md border bg-muted/20 p-3">
                  <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{c.authorName}</span>
                    <span>·</span>
                    <span>{format(new Date(c.createdAt), 'MMM d, yyyy h:mm a')}</span>
                    <span className="ml-auto text-[11px]">{commentAuthorLabel(c)}</span>
                  </div>
                  <p className="whitespace-pre-wrap text-sm">{c.body}</p>
                </div>
              ))
            )}
          </div>
          <div className="space-y-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Post an update for the campaign manager"
              rows={3}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand/40"
            />
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={postComment}
                disabled={!draft.trim() || addComment.isPending}
              >
                <Send className="mr-1 h-3 w-3" /> Post comment
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
